<template>
  <div
    @keydown.down="focusNextListItem($event)"
    @keydown.tab.exact="focusNextListItemOnTab($event)"
    @keydown.tab.shift="focusPreviousListItemOnTab($event)"
    @keydown.up="focusPreviousListItem($event)"
    class="Nav-account-dropdown"
    ref="navAccountDropdown"
  >
    <div class="Nav-account-dropdown__container-close-button">
      <ux-atoms-button :no-border="true" @click="emit('close::dropdown')" class="Nav-account-dropdown__close-button" type="button" variant="action">
        <ux-atoms-icon :description="$t('ux.molecules.modal.close.label')" name="close" size="m" />
      </ux-atoms-button>
    </div>
    <div class="Nav-account-dropdown__logged-container" v-if="userStore.hasLoadedUser">
      <ux-atoms-typo :text="userName" as="p" class="Nav-account-dropdown__member-text" color="dark" variant="text-heading-01" />
      <ux-molecules-loyalty-status :status="loyaltyCard" />
      <div class="Nav-account-dropdown__reward">
        <ux-atoms-typo :text="$t('ux.molecules.navAccountDropdown.pointsRewards') + ':'" as="p" variant="text-regular" />&nbsp;
        <ux-atoms-typo :text="userPoints" as="p" class="Nav-account-dropdown__reward-points" variant="text-regular" />
      </div>
    </div>
    <div class="Nav-account-dropdown__unlogged-container" v-else>
      <div class="Nav-account-dropdown__signin-container">
        <div class="Nav-account-dropdown__signin-title">
          <ux-atoms-icon :name="'logo-small'" class="Nav-account-dropdown__signin-title-icon" />
          <ux-atoms-typo :text="$t('ux.molecules.navAccountDropdown.loginTitle')" as="p" color="dark" variant="text-heading-03" />
        </div>
        <ux-atoms-typo as="p">
          <i18n-t keypath="ux.molecules.navAccountDropdown.description">
            <ux-atoms-link
              :text="$t('ux.molecules.navAccountDropdown.descriptionTextLink')"
              :to="localePath($t('ux.molecules.navAccountDropdown.descriptionLink'))"
              @click="emit('close::dropdown')"
              underline
            />
          </i18n-t>
        </ux-atoms-typo>
        <ux-atoms-link
          :classic="false"
          :text="$t('ux.molecules.navAccountDropdown.createAccount')"
          :to="createAccountLink"
          @click="gtmCreateAccountEvent"
          class="Button Button--m Button--primary"
          rel="nofollow"
        />
      </div>
      <ux-atoms-link
        :classic="false"
        :text="$t('ux.molecules.navAccountDropdown.login')"
        :to="loginLink"
        @click="gtmLoginEvent"
        class="Button Button--m Button--outlined"
        rel="nofollow"
      />
    </div>
    <div class="Nav-account-dropdown__links">
      <ux-atoms-link
        :text="$t('ux.molecules.navAccountDropdown.restaurantsParticipating')"
        :to="localePath($t('ux.molecules.navAccountDropdown.restaurantsParticipatingLink'))"
        @click="emit('close::dropdown')"
        class="Nav-account-dropdown__link Nav-account-dropdown__link--restaurants"
      />
      <ux-atoms-link
        :text="$t('ux.molecules.navAccountDropdown.logout')"
        :to="logoutLink"
        class="Nav-account-dropdown__link"
        v-if="userStore.hasLoadedUser"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { getFocusableElements, getNextFocusableElement, getPreviousFocusableElement } from '~/helpers/accessibility';
import { gtmEvent, gtmPageName } from '~/helpers/gtm';

const localePath = useLocalePath();

const route = useRoute();
const navAccountDropdown = ref();
const focusableListElements = ref<HTMLElement[]>();

interface Emits {
  (e: 'close::dropdown'): void;
}

const emit = defineEmits<Emits>();

const userStore = useUserStore();
const { getAuthUrl } = useAuth();

const createAccountLink = computed(() => {
  return getAuthUrl('signup');
});

const loginLink = computed(() => {
  return getAuthUrl('login');
});

const logoutLink = computed(() => {
  return getAuthUrl('logout');
});

const userName = computed(() => {
  return `${userStore.firstName} ${userStore.lastName}`;
});

const userPoints = computed(() => {
  return userStore.loyaltyNbPoints?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') ?? '0';
});

const loyaltyCard = computed(() => {
  return userStore.loyaltyStatus;
});

const gtmLoginEvent = () => {
  gtmEvent('cta_authentification', {
    pagename: gtmPageName(route.name as string)
  });
};

const gtmCreateAccountEvent = () => {
  gtmEvent('cta_enroll', {
    pagename: gtmPageName(route.name as string)
  });
};

const getListFocusableElements = () => {
  focusableListElements.value = getFocusableElements(navAccountDropdown.value) as HTMLElement[];
};

const focusPreviousListItem = (e: Event) => {
  e.preventDefault();
  if (e?.target) {
    const previousEl = getPreviousFocusableElement(e.target as HTMLElement, focusableListElements.value as HTMLElement[]);
    previousEl.focus();
  }
};

const focusNextListItem = (e: KeyboardEvent) => {
  e.preventDefault();
  if (e?.target) {
    const nextEl = getNextFocusableElement(e.target as HTMLElement, focusableListElements.value as HTMLElement[]);
    nextEl?.focus();
  }
};

const focusNextListItemOnTab = (e: KeyboardEvent) => {
  e.preventDefault();
  if (e?.target) {
    if (focusableListElements.value?.length && e.target === focusableListElements.value[focusableListElements.value.length - 1]) {
      emit('close::dropdown');
    }
    const nextEl = getNextFocusableElement(e.target as HTMLElement, focusableListElements.value as HTMLElement[]);
    nextEl?.focus();
  }
};

const focusPreviousListItemOnTab = (e: KeyboardEvent) => {
  e.preventDefault();
  if (e?.target) {
    if (focusableListElements.value?.length && e.target === focusableListElements.value[0]) {
      emit('close::dropdown');
    }
    const previousEl = getPreviousFocusableElement(e.target as HTMLElement, focusableListElements.value as HTMLElement[]);
    previousEl?.focus();
  }
};

onMounted(() => {
  getListFocusableElements();
});

onUpdated(() => {
  getListFocusableElements();
});
</script>
<style lang="scss" scoped>
@use 'NavAccountDropdown.scss';
</style>
